import * as React from 'react';

import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';

const CLEVER_APP_LINK = (
  <Link href={LinkConstants.CLEVER_APPLICATIONS} target="_blank">
    here
  </Link>
);

const CLEVER_SUPPORT_LINK = (
  <Link href={LinkConstants.CLEVER_SUPPORT} target="_blank">
    Clever support
  </Link>
);

const CLASSLINK_APP_LINK = (
  <Link href={LinkConstants.CLASSLINK_APPLICATIONS} target="_blank">
    here
  </Link>
);

const CLASSLINK_SUPPORT_LINK = (
  <Link href={LinkConstants.CLASSLINK_SUPPORT} target="_blank">
    ClassLink support
  </Link>
);

const SHARING_SETTINGS_LINK_CLEVER = (
  <Link href={LinkConstants.CONNECT_SIS_CLEVER} target="_blank">
    here
  </Link>
);

const SHARING_SETTINGS_LINK_CLASSLINK = (
  <Link href={LinkConstants.CONNECT_SIS_CLASSLINK} target="_blank">
    here
  </Link>
);

export type NextStep = {
  intro: React.ReactNode;
  bullets?: React.ReactNode[];
  footer?: React.ReactNode;
};

export type SyncHoldInformation = {
  error: string;
  reason: string;
  nextStep: NextStep;
};

export type SyncHold =
  | 'CleverDisconnectedHold'
  | 'ClasslinkDisconnectedHold'
  | 'DisconnectedHold'
  | 'TooManyDestructiveChangesHold'
  | 'EmergencyContactHold'
  | 'TooManyContactPointsHold';
type SyncHoldCopyMap = { [K in SyncHold]: SyncHoldInformation | undefined };

export const ApiSyncHoldCopy = (syncMethod: string, hold: SyncHold) => {
  const ApiSyncsHolds: SyncHoldCopyMap = {
    ClasslinkDisconnectedHold: {
      error: 'ClassLink is disconnected',
      reason: 'Your SIS sync has been paused because we are unable to communicate with ClassLink.',
      nextStep: {
        intro: 'Check your settings in ClassLink. Make sure of the following:',
        bullets: [
          <span>
            You are sharing the correct data with Remind. We recommend setting full sharing permissions.
            Learn about sharing settings with Remind {SHARING_SETTINGS_LINK_CLASSLINK}.
          </span>,
          <span>
            You have the Remind app installed in ClassLink. You can view it {CLASSLINK_APP_LINK}.
          </span>,
        ],
        footer: 'Once you’ve addressed the above, resume the sync below.',
      },
    },
    CleverDisconnectedHold: {
      error: 'Clever is disconnected',
      reason: 'Your SIS sync has been paused because we are unable to communicate with Clever.',
      nextStep: {
        intro: 'Check your settings in Clever. Make sure of the following:',
        bullets: [
          <span>
            You are sharing the correct data with Remind. We recommending sharing district data.
            Learn about sharing settings with Remind {SHARING_SETTINGS_LINK_CLEVER}.
          </span>,
          <span>
            You have the Remind app installed in Clever. You can view it {CLEVER_APP_LINK}.
          </span>,
        ],
        footer: 'Once you’ve addressed the above, resume the sync below.',
      },
    },
    DisconnectedHold : {
      error: '',
      reason: '',
      nextStep: {
        intro: '',
        bullets: [],
        footer: '',
      },
    },
    EmergencyContactHold: {
      error: 'Secondary contacts included',
      reason: `Your SIS sync has been paused because we suspect a data configuration error in ${syncMethod}.`,
      nextStep: {
        intro: `Emergency and secondary contacts aren’t typically contacted by teachers so we do not recommend creating accounts for them. Download the CSV of the additional contacts and review them. If you wish to sync them, create accounts for them below. Otherwise, review your settings in ${syncMethod} to ensure that emergency and secondary contacts are filtered out from sync, then return here to check for new ${syncMethod} data.`,
      },
    },
    TooManyDestructiveChangesHold: {
      error: 'Removed',
      reason: 'Your SIS sync has been paused because we detected a large number of removals.',
      nextStep: {
        intro: (
          <span>
            If the removals are unexpected, contact your SIS Administrator or{' '}
            {syncMethod === 'Classlink' ? CLASSLINK_SUPPORT_LINK : CLEVER_SUPPORT_LINK} and undo the
            removals in your SIS or {syncMethod}, then check for new {syncMethod} data. If the
            removals are expected, confirm them below.
          </span>
        ),
      },
    },
    TooManyContactPointsHold: {
      error: 'Too many parent connections',
      reason: `Your SIS sync has been paused because we suspect a data configuration error in your SIS or in ${syncMethod}.`,
      nextStep: {
        intro: `Accounts in your school data have six or more points of contact associated with them. This could be a mistake in your configuration. Download the CSV of these accounts and review them. If you wish to sync them, resume the sync below. Otherwise, correct them in your SIS or ${syncMethod} and return here to check for new ${syncMethod} data.`,
      },
    },
  };
  if (hold === "DisconnectedHold") {
    if (syncMethod === 'Clever') {
      return ApiSyncsHolds['CleverDisconnectedHold']
    } else if (syncMethod === 'Classlink') {
      return ApiSyncsHolds['ClasslinkDisconnectedHold']
    }
    // should not get here :)
  }
  return ApiSyncsHolds[hold];
};

export const SftpSyncHoldCopy: SyncHoldCopyMap = {
  CleverDisconnectedHold: undefined,
  ClasslinkDisconnectedHold: undefined,
  DisconnectedHold: undefined,
  EmergencyContactHold: undefined,
  TooManyDestructiveChangesHold: {
    error: 'Resources removed',
    reason: 'Your SIS sync has been paused because we detected a large number of removals.',
    nextStep: {
      intro:
        'If the removals are unexpected, click "How to fix my files" below and follow the steps in the help center article to update your CSV files and resend to Remind. If the removals are expected, confirm them below to proceed.',
    },
  },
  TooManyContactPointsHold: {
    error: 'Too many parent connections',
    reason:
      'Your SIS sync has been paused because we suspect a data configuration error in your SIS.',
    nextStep: {
      intro:
        'Accounts in your school data have six or more parents associated with them. This could be a mistake in your configuration. Download the CSV of these accounts and review them. If you wish to sync them, create accounts for them below. Otherwise, update your CSV files to have the proper parent connections, send the new CSV files to Remind, and reload this page to resume the sync. ',
    },
  },
};
